'use client';
import React, { useState, useEffect, useRef } from 'react';

interface GrammarCard {
  id: string;
  title: string;
  tag: string;
  tagColor: string;
  rule: string;
  example: string;
  explanation: string;
  tip?: string;
}

const GRAMMAR_CARDS: GrammarCard[] = [
  {
    id: 'gc-to-be',
    title: 'To Be — Être',
    tag: 'Verbes de base',
    tagColor: 'bg-blue-100 text-blue-700',
    rule: 'I am / You are / He-She-It is / We are / They are',
    example: '"I am French. She is a teacher. They are students."',
    explanation: "En anglais, 'to be' change selon le sujet. Contrairement au français, il n'y a pas d'accent ou de variation complexe — juste am/are/is.",
    tip: "Négatif : I am not → I'm not. She is not → She isn't.",
  },
  {
    id: 'gc-to-have',
    title: 'To Have — Avoir',
    tag: 'Verbes de base',
    tagColor: 'bg-blue-100 text-blue-700',
    rule: 'I have / You have / He-She-It has / We have / They have',
    example: '"I have a car. She has two cats. We have a meeting at 3pm."',
    explanation: "Utilisé pour la possession ET pour former les temps composés (Present Perfect). Attention : 'has' uniquement pour he/she/it.",
    tip: "Négatif : I don't have / She doesn't have.",
  },
  {
    id: 'gc-do-does',
    title: 'Do / Does — Questions et Négations',
    tag: 'Auxiliaires',
    tagColor: 'bg-purple-100 text-purple-700',
    rule: '"Do" pour I/You/We/They — "Does" pour He/She/It',
    example: '"Do you speak English? Does she work here? I don\'t understand."',
    explanation: "En anglais, on ne peut pas former une question ou une négation sans auxiliaire. 'Do/Does' est l'auxiliaire du présent simple.",
    tip: "Avec 'does', le verbe principal perd son 's' : She does work (pas works).",
  },
  {
    id: 'gc-present-simple',
    title: 'Présent Simple vs Continu',
    tag: 'Temps',
    tagColor: 'bg-green-100 text-green-700',
    rule: 'Simple = habitude. Continu (be + -ing) = action en cours maintenant.',
    example: '"I work in Paris." (habitude) vs "I am working from home today." (maintenant)',
    explanation: "Le présent simple décrit ce que vous faites régulièrement. Le présent continu décrit ce qui se passe en ce moment précis.",
    tip: "Indices : always/usually/every day → simple. Now/at the moment/currently → continu.",
  },
  {
    id: 'gc-past-simple',
    title: 'Passé Simple (Past Simple)',
    tag: 'Temps',
    tagColor: 'bg-green-100 text-green-700',
    rule: 'Verbes réguliers : + ed. Irréguliers : à mémoriser.',
    example: '"I worked late yesterday. I went to Canada. She saw a film."',
    explanation: "Pour les actions terminées dans le passé. Les verbes réguliers prennent -ed, mais les irréguliers (go→went, see→saw, buy→bought) sont à apprendre.",
    tip: "Question : Did you go? Négatif : I didn't go (pas 'I didn't went').",
  },
  {
    id: 'gc-past-continuous',
    title: 'Passé Continu (Past Continuous)',
    tag: 'Temps',
    tagColor: 'bg-green-100 text-green-700',
    rule: 'was/were + verbe-ing. Action en cours à un moment précis du passé.',
    example: '"I was reading when she called. They were having dinner at 8pm."',
    explanation: "Le passé continu décrit une action qui était en cours quand une autre action s'est produite. Souvent utilisé avec 'when' (quand) et 'while' (pendant que).",
    tip: "Formule classique : I was doing X when Y happened. While I was sleeping, he arrived.",
  },
  {
    id: 'gc-present-perfect',
    title: 'Present Perfect',
    tag: 'Temps',
    tagColor: 'bg-green-100 text-green-700',
    rule: 'have/has + participe passé. Lien entre passé et présent.',
    example: '"I have lived in Canada for 45 years. She has just arrived."',
    explanation: "Utilisez le Present Perfect quand le passé a un lien avec maintenant. 'I have eaten' (je viens de manger — je n'ai pas faim maintenant).",
    tip: "For = durée. Since = point de départ. Just = vient de. Already = déjà. Yet = encore/déjà (négatif/question).",
  },
  {
    id: 'gc-future-will',
    title: 'Futur avec Will',
    tag: 'Temps',
    tagColor: 'bg-green-100 text-green-700',
    rule: 'will + infinitif (sans to). Décision spontanée, prédiction, promesse.',
    example: '"I will call you tomorrow. It will rain. I\'ll help you with that!"',
    explanation: "Will exprime une décision prise au moment où on parle, une prédiction ou une promesse. C'est le futur le plus simple en anglais.",
    tip: "Négatif : won't (will not). Question : Will you come? Jamais 'will' après 'if' : If it rains, I will stay.",
  },
  {
    id: 'gc-future-going-to',
    title: 'Futur avec Going To',
    tag: 'Temps',
    tagColor: 'bg-green-100 text-green-700',
    rule: 'am/is/are + going to + infinitif. Plan décidé à l\'avance ou preuve visible.',
    example: '"I am going to visit London next month. Look at those clouds — it\'s going to rain!"',
    explanation: "Going to exprime un plan déjà décidé avant le moment où on parle, ou une prédiction basée sur une preuve visible. Différent de 'will' qui est spontané.",
    tip: "Plan prévu → going to. Décision spontanée → will. 'I'm going to study medicine' (décidé). 'I'll have the salad' (spontané).",
  },
  {
    id: 'gc-articles',
    title: 'A / An / The — Les Articles',
    tag: 'Articles',
    tagColor: 'bg-yellow-100 text-yellow-700',
    rule: 'A/An = indéfini (une chose parmi d\'autres). The = défini (une chose précise).',
    example: '"I saw a dog. The dog was big." — "An apple a day." — "The sun is hot."',
    explanation: "A devant consonne (a book), An devant voyelle (an apple). The = on sait de quoi on parle. Pas d'article pour les généralités : 'I like coffee' (pas 'the coffee').",
    tip: "Pas d'article avec les noms de pays (France, Canada) ni les langues (I speak English).",
  },
  {
    id: 'gc-prepositions-time',
    title: 'Prépositions de Temps',
    tag: 'Prépositions',
    tagColor: 'bg-orange-100 text-orange-700',
    rule: 'In = mois/années/saisons. On = jours/dates. At = heures/moments précis.',
    example: '"In July, in 2025, in summer. On Monday, on 14th July. At 3pm, at night."',
    explanation: "La règle est simple : plus la période est grande → in. Un jour précis → on. Un moment ou une heure précise → at.",
    tip: "Exceptions à retenir : at night, at the weekend, in the morning/afternoon/evening.",
  },
  {
    id: 'gc-prepositions-place',
    title: 'Prépositions de Lieu',
    tag: 'Prépositions',
    tagColor: 'bg-orange-100 text-orange-700',
    rule: 'In = à l\'intérieur. On = sur une surface. At = à un endroit précis.',
    example: '"The keys are in the bag. The book is on the table. I am at the office."',
    explanation: "In pour les espaces fermés ou délimités. On pour les surfaces. At pour les lieux précis (adresses, bâtiments, événements).",
    tip: "In a city/country. At a specific address. On a street. At school/work/home (sans article).",
  },
  {
    id: 'gc-since-for',
    title: 'Since vs For',
    tag: 'Prépositions',
    tagColor: 'bg-orange-100 text-orange-700',
    rule: 'Since = depuis un point précis. For = pendant une durée.',
    example: '"I have lived here since 2010." (depuis 2010) — "I have lived here for 14 years." (pendant 14 ans)',
    explanation: "Si vous pouvez répondre 'quand ?', utilisez since. Si vous pouvez répondre 'combien de temps ?', utilisez for. Toujours avec le Present Perfect.",
    tip: "Since 2020 / since Monday / since I was a child. For 5 years / for a week / for a long time.",
  },
  {
    id: 'gc-make-do',
    title: 'Make vs Do',
    tag: 'Verbes',
    tagColor: 'bg-teal-100 text-teal-700',
    rule: 'Make = créer, produire, fabriquer. Do = effectuer une activité, une tâche.',
    example: '"Make a cake, make a decision, make a mistake. Do homework, do sport, do the dishes."',
    explanation: "Make implique de créer quelque chose de nouveau. Do implique d'effectuer une action ou une tâche. Pas de règle absolue — certaines expressions sont fixes.",
    tip: "Make : money, noise, progress, a phone call. Do : business, damage, research, someone a favour.",
  },
  {
    id: 'gc-false-friends',
    title: 'Faux Amis (False Friends)',
    tag: 'Pièges',
    tagColor: 'bg-red-100 text-red-700',
    rule: 'Mots qui ressemblent au français mais signifient autre chose !',
    example: '"Actually" = En fait (pas Actuellement). "Eventually" = Finalement. "Library" = Bibliothèque.',
    explanation: "Les faux amis sont parmi les erreurs les plus fréquentes des francophones. Mémorisez les principaux pour éviter les malentendus embarrassants.",
    tip: "Autres : Sympathetic ≠ Sympathique (= Compréhensif). Sensible ≠ Sensible (= Raisonnable). Deception ≠ Déception (= Tromperie).",
  },
  {
    id: 'gc-questions',
    title: 'Former des Questions',
    tag: 'Structure',
    tagColor: 'bg-indigo-100 text-indigo-700',
    rule: 'Auxiliaire + sujet + verbe. Inversion sujet-verbe obligatoire.',
    example: '"Are you tired? Do you speak French? Have you been to Canada? Can you help me?"',
    explanation: "En anglais, on ne peut pas juste monter la voix pour poser une question. Il faut inverser l'auxiliaire et le sujet. Chaque temps a son auxiliaire.",
    tip: "WH-questions : What, Where, When, Who, Why, How — puis même structure : What do you do?",
  },
  {
    id: 'gc-conditionals',
    title: 'Le Conditionnel (1er et 2ème)',
    tag: 'Temps',
    tagColor: 'bg-green-100 text-green-700',
    rule: 'Réel : If + présent → will. Hypothétique : If + passé → would.',
    example: '"If I study, I will improve." — "If I studied more, I would improve faster."',
    explanation: "Le conditionnel réel parle d'une situation possible. Le conditionnel hypothétique parle d'une situation imaginaire ou peu probable.",
    tip: "Jamais 'would' dans la clause 'if' : ✗ If I would study → ✓ If I studied.",
  },
  {
    id: 'gc-third-conditional',
    title: 'Le 3ème Conditionnel',
    tag: 'Temps',
    tagColor: 'bg-green-100 text-green-700',
    rule: 'If + had + participe passé → would have + participe passé.',
    example: '"If I had studied harder, I would have passed the exam."',
    explanation: "Le 3ème conditionnel parle d'une situation passée qui ne s'est pas produite — un regret ou une hypothèse sur le passé. C'est le conditionnel le plus complexe.",
    tip: "Regret : If I had known, I would have acted differently. Souvent contracté : I'd have, she'd have.",
  },
  {
    id: 'gc-passive-voice',
    title: 'La Voix Passive',
    tag: 'Structure',
    tagColor: 'bg-indigo-100 text-indigo-700',
    rule: 'be + participe passé. Le sujet subit l\'action au lieu de la faire.',
    example: '"The report was written by Marc. English is spoken worldwide. The meeting has been cancelled."',
    explanation: "La voix passive met l'accent sur l'action ou l'objet plutôt que sur l'auteur. Très utilisée dans les contextes formels, professionnels et scientifiques.",
    tip: "Actif : Marc wrote the report. Passif : The report was written by Marc. 'By' introduit l'auteur (optionnel).",
  },
  {
    id: 'gc-reported-speech',
    title: 'Le Discours Rapporté',
    tag: 'Structure',
    tagColor: 'bg-indigo-100 text-indigo-700',
    rule: 'Recul d\'un temps en arrière. Changement de pronoms et d\'expressions de temps.',
    example: '"She said: I am tired." → "She said (that) she was tired."',
    explanation: "Quand on rapporte ce que quelqu'un a dit, le temps verbal recule d'un cran : présent → passé, passé → past perfect. Les pronoms changent aussi.",
    tip: "say → said (sans objet). tell → told (avec objet : He told me). Today → that day. Tomorrow → the next day.",
  },
  {
    id: 'gc-irregular-plurals',
    title: 'Pluriels Irréguliers',
    tag: 'Pièges',
    tagColor: 'bg-red-100 text-red-700',
    rule: 'Certains pluriels ne prennent pas de -s.',
    example: 'Child → Children. Man → Men. Woman → Women. Mouse → Mice. Foot → Feet. Tooth → Teeth.',
    explanation: "Ces pluriels sont hérités du vieil anglais. Ils sont très fréquents — surtout 'children', 'men' et 'women' que vous utiliserez tous les jours.",
    tip: "Autres : Person → People. Sheep → Sheep (inchangé). Fish → Fish (inchangé). Knife → Knives.",
  },
  {
    id: 'gc-modals',
    title: 'Les Modaux',
    tag: 'Auxiliaires',
    tagColor: 'bg-purple-100 text-purple-700',
    rule: 'Can, Must, Should, Would, May/Might — jamais de -s, jamais de to.',
    example: '"I can speak English. You must try. She should practice. It might rain."',
    explanation: "Les modaux sont des auxiliaires spéciaux. Ils expriment la capacité (can), l'obligation (must), le conseil (should), la possibilité (may/might).",
    tip: "Jamais 'to' après un modal : ✗ I can to speak → ✓ I can speak. Pas de -s : ✗ She cans → ✓ She can.",
  },
  {
    id: 'gc-phrasal-verbs',
    title: 'Les Verbes à Particule (Phrasal Verbs)',
    tag: 'Verbes',
    tagColor: 'bg-teal-100 text-teal-700',
    rule: 'Verbe + particule (up, out, on, off, in…). Le sens change complètement.',
    example: '"Give up = abandonner. Look up = chercher. Turn on = allumer. Break down = tomber en panne."',
    explanation: "Les phrasal verbs sont omniprésents en anglais parlé. Leur sens est souvent imprévisible — il faut les apprendre comme des expressions fixes.",
    tip: "Essentiels : give up, look forward to, put off, carry on, find out, get on with, run out of, take off.",
  },
  {
    id: 'gc-comparatives',
    title: 'Comparatifs et Superlatifs',
    tag: 'Structure',
    tagColor: 'bg-indigo-100 text-indigo-700',
    rule: 'Court : -er / the -est. Long (2+ syllabes) : more / the most.',
    example: '"Faster, the fastest. More interesting, the most interesting. Better, the best."',
    explanation: "Pour les adjectifs courts (1 syllabe) : ajoutez -er/-est. Pour les longs : utilisez more/most. Quelques irréguliers : good→better→best, bad→worse→worst.",
    tip: "Comparaison : She is taller than me. Superlatif : She is the tallest in the class. As…as : He is as tall as me.",
  },
  {
    id: 'gc-question-tags',
    title: 'Les Question Tags',
    tag: 'Structure',
    tagColor: 'bg-indigo-100 text-indigo-700',
    rule: 'Phrase positive → tag négatif. Phrase négative → tag positif.',
    example: '"It\'s cold today, isn\'t it? You don\'t like coffee, do you? She can swim, can\'t she?"',
    explanation: "Les question tags sont très courants en anglais britannique. Ils servent à demander confirmation. La règle est simple : si la phrase est positive, le tag est négatif, et vice versa.",
    tip: "I am → aren't I? (exception). Let's go, shall we? You'd better hurry, hadn't you?",
  },
  {
    id: 'gc-gerund-infinitive',
    title: 'Gérondif vs Infinitif',
    tag: 'Verbes',
    tagColor: 'bg-teal-100 text-teal-700',
    rule: 'Certains verbes → -ing. D\'autres → to + infinitif. Certains → les deux.',
    example: '"I enjoy swimming. I want to swim. I like swimming / to swim."',
    explanation: "Après certains verbes, on utilise le gérondif (-ing) : enjoy, avoid, finish, suggest. Après d'autres, l'infinitif : want, need, decide, hope. Certains acceptent les deux.",
    tip: "Gérondif : enjoy, avoid, finish, mind, suggest, keep. Infinitif : want, need, decide, hope, plan, manage.",
  },
  {
    id: 'gc-countable-uncountable',
    title: 'Noms Dénombrables vs Indénombrables',
    tag: 'Articles',
    tagColor: 'bg-yellow-100 text-yellow-700',
    rule: 'Dénombrables : a/an, pluriel en -s. Indénombrables : pas d\'article indéfini, pas de pluriel.',
    example: '"A book, two books. BUT: water (not a water), information (not informations)."',
    explanation: "Les noms indénombrables ne peuvent pas être comptés directement. On utilise des quantificateurs : some water, a glass of water, a piece of information.",
    tip: "Indénombrables courants : advice, information, news, furniture, luggage, money, traffic, weather, work.",
  },
  {
    id: 'gc-some-any',
    title: 'Some vs Any',
    tag: 'Articles',
    tagColor: 'bg-yellow-100 text-yellow-700',
    rule: 'Some = phrases affirmatives. Any = questions et négations.',
    example: '"I have some coffee. Do you have any coffee? I don\'t have any coffee."',
    explanation: "Some s'utilise dans les phrases positives et dans les offres/demandes polies. Any s'utilise dans les questions et les négations.",
    tip: "Offre polie : Would you like some tea? Demande polie : Could I have some water? Any dans les questions générales.",
  },
  {
    id: 'gc-much-many',
    title: 'Much vs Many vs A Lot Of',
    tag: 'Articles',
    tagColor: 'bg-yellow-100 text-yellow-700',
    rule: 'Much + indénombrable. Many + dénombrable. A lot of = les deux.',
    example: '"Much time, much money. Many people, many books. A lot of time, a lot of people."',
    explanation: "Much et many s'utilisent surtout dans les questions et négations. A lot of (ou lots of) s'utilise dans les phrases positives et est plus naturel à l'oral.",
    tip: "How much? (indénombrable) vs How many? (dénombrable). Too much / too many = trop de.",
  },
  {
    id: 'gc-used-to',
    title: 'Used To — Habitudes Passées',
    tag: 'Temps',
    tagColor: 'bg-green-100 text-green-700',
    rule: 'used to + infinitif. Habitude ou état passé qui n\'existe plus.',
    example: '"I used to live in Paris. She used to smoke but she stopped. We used to be friends."',
    explanation: "Used to décrit quelque chose que vous faisiez régulièrement dans le passé mais que vous ne faites plus. Il n'y a pas d'équivalent exact en français — on traduit souvent par l'imparfait.",
    tip: "Question : Did you use to...? Négatif : I didn't use to... Ne pas confondre avec 'be used to' (être habitué à) + -ing.",
  },
  {
    id: 'gc-wish',
    title: 'Wish — Exprimer des Regrets',
    tag: 'Structure',
    tagColor: 'bg-indigo-100 text-indigo-700',
    rule: 'Wish + passé simple (présent). Wish + past perfect (passé). Wish + would (futur).',
    example: '"I wish I spoke better English. I wish I had studied more. I wish it would stop raining."',
    explanation: "Wish exprime un désir ou un regret. Après wish, on utilise un temps en arrière par rapport à la réalité : présent → passé simple, passé → past perfect.",
    tip: "If only = wish (plus fort, plus émotionnel). If only I had more time! If only I had listened!",
  },
  {
    id: 'gc-linking-words',
    title: 'Mots de Liaison',
    tag: 'Structure',
    tagColor: 'bg-indigo-100 text-indigo-700',
    rule: 'Addition : and, also, moreover. Contraste : but, however, although. Cause : because, since, as.',
    example: '"I love English. However, it is difficult. I study hard because I want to improve."',
    explanation: "Les mots de liaison donnent de la fluidité à votre discours. Ils montrent les relations logiques entre les idées. Essentiels pour un anglais naturel et sophistiqué.",
    tip: "Résultat : therefore, so, as a result. Exemple : for example, such as. Concession : although, even though, despite.",
  },
  {
    id: 'gc-irregular-verbs',
    title: 'Verbes Irréguliers Essentiels',
    tag: 'Verbes',
    tagColor: 'bg-teal-100 text-teal-700',
    rule: 'Infinitif → Passé simple → Participe passé.',
    example: '"Go→went→gone. See→saw→seen. Take→took→taken. Write→wrote→written. Know→knew→known."',
    explanation: "Les verbes irréguliers n'ont pas de règle — il faut les mémoriser. Les plus fréquents reviennent constamment dans la conversation quotidienne.",
    tip: "Autres essentiels : buy→bought→bought. Think→thought→thought. Bring→brought→brought. Teach→taught→taught.",
  },
  {
    id: 'gc-adverbs',
    title: 'Les Adverbes de Fréquence',
    tag: 'Structure',
    tagColor: 'bg-indigo-100 text-indigo-700',
    rule: 'Always > Usually > Often > Sometimes > Rarely > Never. Placés avant le verbe principal.',
    example: '"I always drink coffee. She usually works late. He never eats meat."',
    explanation: "Les adverbes de fréquence se placent avant le verbe principal mais après 'to be'. Ils indiquent à quelle fréquence quelque chose se produit.",
    tip: "Avec 'to be' : She is always late. Avec autres verbes : She always arrives late. Jamais entre sujet et auxiliaire.",
  },
  {
    id: 'gc-so-such',
    title: 'So vs Such',
    tag: 'Structure',
    tagColor: 'bg-indigo-100 text-indigo-700',
    rule: 'So + adjectif/adverbe. Such + (a/an) + nom.',
    example: '"It is so cold! She is such a good teacher! They work so hard. It was such fun!"',
    explanation: "So et such expriment tous les deux l'intensité, mais leur structure diffère. So précède un adjectif seul. Such précède un groupe nominal.",
    tip: "So + adj : so beautiful. Such + a + adj + noun : such a beautiful day. Such + noun (pluriel/indénombrable) : such good news.",
  },
];

const TAGS = ['Tous', 'Verbes de base', 'Auxiliaires', 'Temps', 'Articles', 'Prépositions', 'Verbes', 'Pièges', 'Structure'];

export default function GrammarCards() {
  const [activeTag, setActiveTag] = useState('Tous');
  const [expandedId, setExpandedId] = useState<string | null>('gc-to-be');
  const [speaking, setSpeaking] = useState<string | null>(null);
  const voicesRef = useRef<SpeechSynthesisVoice[]>([]);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;

    const loadVoices = () => {
      const v = window.speechSynthesis.getVoices();
      if (v.length > 0) voicesRef.current = v;
    };

    loadVoices();
    window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
    };
  }, []);

  const speak = (text: string, cardId: string) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;

    window.speechSynthesis.cancel();
    setSpeaking(cardId);

    const clean = text.replace(/^["«\u201C]|["»\u201D]$/g, '').trim();

    const doSpeak = () => {
      const utterance = new SpeechSynthesisUtterance(clean);
      utterance.lang = 'en-GB';
      utterance.rate = 0.85;
      utterance.pitch = 1;

      const voices = voicesRef.current.length > 0
        ? voicesRef.current
        : window.speechSynthesis.getVoices();

      const enVoice =
        voices.find((v) => v.lang === 'en-GB') ||
        voices.find((v) => v.lang.startsWith('en'));
      if (enVoice) utterance.voice = enVoice;

      utterance.onend = () => setSpeaking(null);
      utterance.onerror = () => setSpeaking(null);

      window.speechSynthesis.speak(utterance);
    };

    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
    }

    setTimeout(doSpeak, 100);
  };

  const filtered = activeTag === 'Tous'
    ? GRAMMAR_CARDS
    : GRAMMAR_CARDS.filter((c) => c.tag === activeTag);

  const tagCounts: Record<string, number> = { Tous: GRAMMAR_CARDS.length };
  GRAMMAR_CARDS.forEach((c) => {
    tagCounts[c.tag] = (tagCounts[c.tag] || 0) + 1;
  });

  return (
    <section className="py-12 lg:py-16">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        {/* Filter pills */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 mb-8">
          {TAGS.map((tag) => (
            <button
              key={`gram-tag-${tag}`}
              onClick={() => setActiveTag(tag)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm transition-all duration-150 ${
                activeTag === tag ? 'pill-active shadow-sm' : 'pill-inactive'
              }`}
            >
              {tag}
              {tagCounts[tag] && (
                <span className="text-xs font-tabular opacity-70">{tagCounts[tag]}</span>
              )}
            </button>
          ))}
        </div>

        {/* Results info */}
        <p className="text-sm text-muted-foreground mb-6">
          <span className="font-semibold text-primary font-tabular">{filtered.length}</span> règle{filtered.length !== 1 ? 's' : ''} de grammaire
        </p>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-5">
          {filtered.map((card) => {
            const isOpen = expandedId === card.id;
            return (
              <div
                key={card.id}
                className={`bg-white border-2 rounded-2xl overflow-hidden transition-all duration-200 ${
                  isOpen ? 'border-accent shadow-amber' : 'border-border shadow-card hover:border-accent/40 hover:shadow-card-hover'
                }`}
              >
                {/* Card header — always visible */}
                <button
                  onClick={() => setExpandedId(isOpen ? null : card.id)}
                  className="w-full text-left px-5 py-4 flex items-start justify-between gap-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${card.tagColor}`}>
                        {card.tag}
                      </span>
                    </div>
                    <h3 className="text-base font-bold text-primary">{card.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1 font-mono leading-relaxed line-clamp-2">
                      {card.rule}
                    </p>
                  </div>
                  <svg
                    className={`w-5 h-5 text-accent flex-shrink-0 mt-0.5 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Expanded content */}
                <div
                  className={`overflow-hidden transition-all duration-250 ${isOpen ? 'max-h-96' : 'max-h-0'}`}
                >
                  <div className="px-5 pb-5 space-y-3 border-t border-border pt-4">
                    {/* Example */}
                    <div className="bg-primary/5 rounded-xl px-4 py-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <p className="text-xs font-semibold text-accent uppercase tracking-wider">Exemple</p>
                        <button
                          onClick={(e) => { e.stopPropagation(); speak(card.example, card.id); }}
                          className={`w-7 h-7 rounded-lg flex items-center justify-center transition-colors group ${
                            speaking === card.id
                              ? 'bg-accent/20 animate-pulse' : 'bg-muted hover:bg-accent/10'
                          }`}
                          aria-label="Écouter l'exemple"
                          title="Écouter la prononciation"
                        >
                          <svg className={`w-3.5 h-3.5 transition-colors ${speaking === card.id ? 'text-accent' : 'text-muted-foreground group-hover:text-accent'}`} fill="currentColor" viewBox="0 0 24 24">
                            <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02z"/>
                          </svg>
                        </button>
                      </div>
                      <p className="text-sm font-medium text-primary italic leading-relaxed">{card.example}</p>
                    </div>

                    {/* Explanation */}
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">Explication</p>
                      <p className="text-sm text-secondary leading-relaxed">{card.explanation}</p>
                    </div>

                    {/* Tip */}
                    {card.tip && (
                      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                        <p className="text-xs font-semibold text-accent uppercase tracking-wider mb-1">💡 Astuce</p>
                        <p className="text-xs text-secondary leading-relaxed">{card.tip}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}